import type { AppData, MetricRow } from '../data/types';

const NAVS_ASPIRACIONALES = [
  { id: 'Command Center', icon: '🎯', desc: 'Vista 360 del estado actual del hospital' },
  { id: 'Compliance', icon: '✅', desc: 'Cumplimiento regulatorio (Res. 3100, JCI, ICONTEC)' },
  { id: 'Behavior', icon: '🔄', desc: 'Comportamiento y patrones del proceso clínico' },
  { id: 'Historical', icon: '📈', desc: 'Series temporales y análisis retrospectivo' },
  { id: 'Monitoring', icon: '📡', desc: 'Indicadores en tiempo casi real con umbrales' },
  { id: 'Explorer', icon: '🔍', desc: 'Análisis ad-hoc y exploración libre' },
];

type YesMaybe = 'yes' | 'maybe' | '';

const NIVEL_APLICA: Record<string, Record<string, YesMaybe>> = {
  N0: { 'Command Center': '', Compliance: '', Behavior: '', Historical: '', Monitoring: '', Explorer: 'yes' },
  N1: { 'Command Center': '', Compliance: '', Behavior: '', Historical: 'maybe', Monitoring: 'yes', Explorer: 'yes' },
  N2: { 'Command Center': '', Compliance: 'maybe', Behavior: 'yes', Historical: 'yes', Monitoring: '', Explorer: '' },
  N3: { 'Command Center': 'maybe', Compliance: 'yes', Behavior: 'maybe', Historical: '', Monitoring: '', Explorer: '' },
  N4: { 'Command Center': 'yes', Compliance: 'maybe', Behavior: '', Historical: '', Monitoring: '', Explorer: '' },
};

const AUDIENCIAS = ['Strategic', 'Quality Management', 'Tactic', 'Operative'] as const;

const AUDIENCIA_PCT: Record<string, Record<string, number>> = {
  Strategic:           { 'Command Center': 75, Compliance: 5,  Behavior: 10, Historical: 5,  Monitoring: 4,  Explorer: 1 },
  'Quality Management':{ 'Command Center': 10, Compliance: 70, Behavior: 5,  Historical: 5,  Monitoring: 0,  Explorer: 0 },
  Tactic:              { 'Command Center': 5,  Compliance: 5,  Behavior: 70, Historical: 10, Monitoring: 5,  Explorer: 5 },
  Operative:           { 'Command Center': 1,  Compliance: 4,  Behavior: 10, Historical: 5,  Monitoring: 65, Explorer: 15 },
};

const REFRESH: Record<string, { mode: string; cadence: string }> = {
  'Command Center': { mode: 'Batch', cadence: 'Weekly' },
  Compliance:       { mode: 'Batch', cadence: 'Weekly' },
  Behavior:         { mode: 'Batch', cadence: 'Daily' },
  Historical:       { mode: 'Batch', cadence: 'Weekly' },
  Monitoring:       { mode: 'Real Time', cadence: '' },
  Explorer:         { mode: 'Batch / Real Time', cadence: '' },
};

const TIMEFRAME: Record<string, { tipo: string; rango: string }> = {
  'Command Center': { tipo: 'Periodo',        rango: '3–36 meses' },
  Compliance:       { tipo: 'Periodo',        rango: 'Quarter' },
  Behavior:         { tipo: 'Periodo',        rango: 'Monthly / Weekly' },
  Historical:       { tipo: 'Inter-periodos', rango: 'Monthly, Quarter, Annual (Usuario elige)' },
  Monitoring:       { tipo: 'Turno',          rango: '' },
  Explorer:         { tipo: 'Turno',          rango: 'Extensible a Monthly según dominio' },
};

const NIVELES = [
  { n: 'N0', label: 'Dato', desc: 'Hecho atómico raw del transaccional. Existe en abundancia, no documentado.' },
  { n: 'N1', label: 'Métrica', desc: 'Cálculo catalogado: conteo, suma, promedio con descripción. Donde vivimos hoy.' },
  { n: 'N2', label: 'Monitoreo', desc: 'Métrica perfilada con contexto + visualización productiva. En construcción.' },
  { n: 'N3', label: 'Desempeño', desc: 'N2 + definiciones formales + justificación + meta + umbrales + owner.' },
  { n: 'N4', label: 'KPI', desc: 'N3 + vínculo a objetivo estratégico institucional.' },
];

// Nomenclatura del inventario ya está alineada con las navegaciones Pulse
// (renombre aplicado al CSV fuente 2026-05-26: Strategic→Behavior, Operational→Explorer).
// Hoy solo hay contenido en 3 navegaciones: Historical, Behavior, Explorer.
// Command Center, Compliance y Monitoring siguen sin contenido.
const DASHTYPE_MAP_TO_NAV: Record<string, string> = {
  Historical: 'Historical',
  Behavior: 'Behavior',
  Explorer: 'Explorer',
};

function navColor(nav: string): string {
  const map: Record<string, string> = {
    'Command Center': 'bg-rose-50 border-rose-200 text-rose-900',
    Behavior: 'bg-violet-50 border-violet-200 text-violet-900',
    Compliance: 'bg-amber-50 border-amber-200 text-amber-900',
    Historical: 'bg-blue-50 border-blue-200 text-blue-900',
    Monitoring: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    Explorer: 'bg-slate-100 border-slate-300 text-slate-900',
  };
  return map[nav] || 'bg-slate-50 border-slate-200';
}

export function renderVision(container: HTMLElement, data: AppData): void {
  const metrics = data.metrics;
  const totalKPIs = metrics.length;

  const navCountsRaw: Record<string, number> = {};
  const navProducts: Record<string, Set<string>> = {};
  for (const m of metrics) {
    const mappedNav = DASHTYPE_MAP_TO_NAV[m.DashboardType] || m.DashboardType;
    if (!mappedNav) continue;
    navCountsRaw[mappedNav] = (navCountsRaw[mappedNav] || 0) + 1;
    if (!navProducts[mappedNav]) navProducts[mappedNav] = new Set();
    if (m.Product) navProducts[mappedNav].add(m.Product);
  }

  const productosUnicos = Array.from(new Set(metrics.map((m) => m.Product).filter(Boolean))).sort();
  const modulosUnicos = new Set(metrics.map((m) => `${m.Product}::${m.Module}`).filter(Boolean));
  const ejemploRows = metrics.filter((m) => m.Product === 'Facturación' && m.Module === 'Facturación General' && m.KPIDescription);

  container.innerHTML = `
    <div class="max-w-6xl mx-auto py-10 px-8 space-y-16">

      ${renderHero(totalKPIs, productosUnicos.length, modulosUnicos.size)}

      ${renderSection1()}

      ${renderSection2(navCountsRaw, navProducts, totalKPIs)}

      ${renderSection3(totalKPIs)}

      ${renderSection4(metrics, data)}

      ${renderSection5(ejemploRows)}

      ${renderFooter()}
    </div>
  `;
}

function renderHero(totalKPIs: number, productos: number, modulos: number): string {
  return `
    <div class="text-center space-y-3 pb-8 border-b border-slate-200">
      <div class="text-xs font-semibold uppercase tracking-widest text-pulse-600">Pulse · Visión & Estado</div>
      <h1 class="text-4xl font-bold text-slate-900 tracking-tight">Lo que es, lo que será, y lo que falta</h1>
      <p class="text-base text-slate-600 max-w-2xl mx-auto">
        Pulse es la suite analítica clínica de Indigo. Esta vista cuenta en 5 pasos hacia dónde vamos, dónde estamos hoy, y muestra un ejemplo concreto.
      </p>
      <div class="flex justify-center gap-8 pt-4 text-sm text-slate-500">
        <span><strong class="text-slate-900">${totalKPIs}</strong> KPIs catalogados</span>
        <span>·</span>
        <span><strong class="text-slate-900">${productos}</strong> productos</span>
        <span>·</span>
        <span><strong class="text-slate-900">${modulos}</strong> módulos</span>
      </div>
    </div>
  `;
}

function renderSection1(): string {
  // ----- helpers -----
  const navHeader = NAVS_ASPIRACIONALES.map(
    (nav) => `
      <th class="px-2 py-3 text-center font-semibold text-slate-700 border-l border-slate-200 min-w-[110px]">
        <div class="text-lg leading-none">${nav.icon}</div>
        <div class="text-[11px] mt-1">${nav.id}</div>
      </th>
    `,
  ).join('');

  const yesMaybeCell = (v: YesMaybe): string => {
    if (v === 'yes') {
      return `<td class="px-2 py-2 text-center border-l border-slate-100 bg-emerald-50">
        <span class="badge badge-green font-semibold">Yes</span>
      </td>`;
    }
    if (v === 'maybe') {
      return `<td class="px-2 py-2 text-center border-l border-slate-100 bg-amber-50">
        <span class="badge badge-amber font-semibold">Maybe</span>
      </td>`;
    }
    return `<td class="px-2 py-2 text-center border-l border-slate-100 text-slate-300">·</td>`;
  };

  const pctCell = (v: number): string => {
    if (v === 0) {
      return `<td class="px-2 py-2 text-center border-l border-slate-100">
        <div class="text-[11px] text-slate-300 font-mono">0%</div>
      </td>`;
    }
    let bg = 'bg-yellow-50 text-yellow-900';
    if (v >= 60) bg = 'bg-emerald-500 text-white';
    else if (v >= 30) bg = 'bg-emerald-300 text-emerald-900';
    else if (v >= 15) bg = 'bg-emerald-100 text-emerald-800';
    else if (v >= 6) bg = 'bg-lime-100 text-lime-800';
    return `<td class="px-2 py-2 text-center border-l border-slate-100">
      <div class="${bg} rounded-md py-1.5 font-bold text-sm">${v}%</div>
    </td>`;
  };

  // ----- subsections -----
  const nivelesRows = NIVELES.map(
    (n) => `
      <tr class="border-t border-slate-100 hover:bg-slate-50">
        <td class="px-4 py-2 text-xs text-slate-700">
          <span class="font-mono font-semibold text-pulse-700">${n.n}</span>
          <span class="ml-2">${n.label}</span>
        </td>
        ${NAVS_ASPIRACIONALES.map((nav) => yesMaybeCell(NIVEL_APLICA[n.n][nav.id] || '')).join('')}
      </tr>
    `,
  ).join('');

  const audienciaRows = AUDIENCIAS.map(
    (aud) => `
      <tr class="border-t border-slate-100 hover:bg-slate-50">
        <td class="px-4 py-2 text-xs text-slate-700 font-medium">${aud}</td>
        ${NAVS_ASPIRACIONALES.map((nav) => pctCell(AUDIENCIA_PCT[aud][nav.id] || 0)).join('')}
      </tr>
    `,
  ).join('');

  const refreshRow = `
    <tr class="border-t border-slate-100 hover:bg-slate-50">
      <td class="px-4 py-2 text-xs text-slate-700 font-medium">Refresh</td>
      ${NAVS_ASPIRACIONALES.map((nav) => {
        const r = REFRESH[nav.id];
        const isRealTime = r.mode.includes('Real');
        const cls = isRealTime ? 'bg-rose-100 text-rose-800' : 'bg-sky-100 text-sky-800';
        return `<td class="px-2 py-2 text-center border-l border-slate-100">
          <span class="badge ${cls} font-semibold">${r.mode}</span>
          ${r.cadence ? `<div class="text-[10px] text-slate-500 mt-1">${r.cadence}</div>` : ''}
        </td>`;
      }).join('')}
    </tr>
  `;

  const timeframeRow = `
    <tr class="border-t border-slate-100 hover:bg-slate-50">
      <td class="px-4 py-2 text-xs text-slate-700 font-medium">TimeFrame</td>
      ${NAVS_ASPIRACIONALES.map((nav) => {
        const t = TIMEFRAME[nav.id];
        return `<td class="px-2 py-2 text-center border-l border-slate-100">
          <div class="text-[11px] font-semibold text-slate-700">${t.tipo}</div>
          ${t.rango ? `<div class="text-[10px] text-slate-500 mt-0.5 leading-tight">${t.rango}</div>` : ''}
        </td>`;
      }).join('')}
    </tr>
  `;

  const sectionHeaderRow = (label: string, sublabel: string) => `
    <tr class="bg-slate-100">
      <td colspan="${1 + NAVS_ASPIRACIONALES.length}" class="px-4 py-2">
        <div class="text-[10px] font-bold uppercase tracking-wider text-slate-700">${label}</div>
        <div class="text-[11px] text-slate-500">${sublabel}</div>
      </td>
    </tr>
  `;

  const NIVEL_COLOR: Record<string, string> = {
    N0: 'border-slate-300 bg-slate-50',
    N1: 'border-blue-300 bg-blue-50',
    N2: 'border-pulse-300 bg-pulse-50',
    N3: 'border-emerald-300 bg-emerald-50',
    N4: 'border-amber-300 bg-amber-50',
  };

  const nivelesEscalera = `
    <div class="bg-white rounded-xl border border-slate-200 p-5 mb-6">
      <div class="flex items-baseline justify-between mb-4">
        <div>
          <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider">Niveles del dato</h3>
          <p class="text-xs text-slate-500 mt-1">Escalera de madurez. Cada nivel suma sobre el anterior.</p>
        </div>
        <div class="text-[10px] text-slate-400 uppercase tracking-widest">crudo → estratégico →</div>
      </div>
      <div class="grid grid-cols-5 gap-2">
        ${NIVELES.map((n, i) => `
          <div class="relative rounded-lg border-2 ${NIVEL_COLOR[n.n]} p-3 ${i > 0 ? 'ml-0' : ''}">
            ${i > 0 ? `<div class="absolute -left-2 top-1/2 -translate-y-1/2 text-slate-300 text-base font-bold pointer-events-none">›</div>` : ''}
            <div class="flex items-baseline gap-2">
              <span class="text-base font-mono font-bold text-slate-900">${n.n}</span>
              <span class="text-sm font-semibold text-slate-800">${n.label}</span>
            </div>
            <p class="text-[11px] text-slate-600 mt-2 leading-snug">${n.desc}</p>
          </div>
        `).join('')}
      </div>
    </div>
  `;

  return `
    <section>
      <div class="flex items-baseline gap-3 mb-3">
        <span class="text-pulse-600 font-mono text-sm font-semibold">§ 1</span>
        <h2 class="text-2xl font-bold text-slate-900">Cómo imaginamos Pulse</h2>
      </div>
      <p class="text-slate-600 mb-6">
        Pulse se piensa en dos ejes: <strong>5 niveles de madurez del dato</strong> (qué tan refinado está cada indicador)
        y <strong>6 navegaciones</strong> (cómo se accede a él, para quién, con qué frecuencia). Primero los niveles, después la matriz cruzada.
      </p>

      ${nivelesEscalera}

      <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">Matriz de visión — Navegación × 4 dimensiones</h3>
      <p class="text-slate-600 text-sm mb-4">
        Cada navegación se caracteriza por qué niveles del dato muestra, qué audiencia la usa más, con qué cadencia se refresca, y qué horizonte temporal cubre.
        Este es el contrato de diseño.
      </p>
      <div class="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table class="w-full text-xs">
          <thead class="bg-slate-50">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-slate-700 w-56">Dimensión</th>
              ${navHeader}
            </tr>
          </thead>
          <tbody>
            ${sectionHeaderRow('Niveles aplicables', '¿Qué dato se debe mostrar en cada tipo de Navegación?')}
            ${nivelesRows}

            ${sectionHeaderRow('Audiencia · % de tiempo', '¿Cuánto tiempo se estima que pasará cada Audiencia por Navegación?')}
            ${audienciaRows}

            ${sectionHeaderRow('Cadencia técnica', '¿Cada cuánto se actualiza? ¿Qué tanta historia se ve?')}
            ${refreshRow}
            ${timeframeRow}
          </tbody>
        </table>
        <div class="px-4 py-2 bg-slate-50 text-[11px] text-slate-500 border-t border-slate-200">
          Heatmap de audiencia: verde oscuro = audiencia primaria (≥60%); verde medio 30–60%; verde claro 15–30%; amarillo &lt;15%.
        </div>
      </div>
    </section>
  `;
}

function renderSection1Legacy(navCounts: Record<string, number>, total: number): string {
  const headerNiveles = NIVELES
    .map(
      (n) => `<th class="px-3 py-3 text-center font-semibold text-slate-700 border-l border-slate-200">
        <div class="text-base">${n.n}</div>
        <div class="text-[10px] font-medium text-slate-500 normal-case mt-0.5">${n.label}</div>
      </th>`,
    )
    .join('');

  const maxCount = Math.max(1, ...Object.values(navCounts));

  const cellRender = (navId: string, level: string): string => {
    const navCount = navCounts[navId] || 0;

    // N0 (raw) existe en abundancia pero no documentado — renderizado especial
    if (level === 'N0') {
      if (navCount === 0) {
        return `<td class="px-2 py-3 text-center border-l border-slate-100">
          <div class="text-[11px] text-slate-300 font-mono">—</div>
          <div class="text-[10px] text-slate-300 mt-0.5">·</div>
        </td>`;
      }
      return `<td class="px-2 py-2 text-center border-l border-slate-100">
        <div class="bg-slate-200 text-slate-700 rounded-md py-1.5 px-1 mx-auto border border-dashed border-slate-400">
          <div class="text-sm font-bold leading-none">∞</div>
          <div class="text-[10px] opacity-80 mt-1 leading-none">sin documentar</div>
        </div>
      </td>`;
    }

    // N1 es el único con contenido catalogado hoy
    const count = level === 'N1' ? navCount : 0;
    const pct = total ? (count / total) * 100 : 0;

    if (count === 0) {
      return `<td class="px-2 py-3 text-center border-l border-slate-100">
        <div class="text-[11px] text-slate-300 font-mono">0%</div>
        <div class="text-[10px] text-slate-300 mt-0.5">·</div>
      </td>`;
    }

    const ratio = count / maxCount;
    let bg = 'bg-pulse-50 text-pulse-800';
    if (ratio >= 0.75) bg = 'bg-pulse-600 text-white';
    else if (ratio >= 0.5) bg = 'bg-pulse-400 text-white';
    else if (ratio >= 0.25) bg = 'bg-pulse-200 text-pulse-900';
    else if (ratio >= 0.1) bg = 'bg-pulse-100 text-pulse-800';

    return `<td class="px-2 py-2 text-center border-l border-slate-100">
      <div class="${bg} rounded-md py-1.5 px-1 mx-auto">
        <div class="text-sm font-bold leading-none">${pct.toFixed(0)}%</div>
        <div class="text-[10px] opacity-80 mt-1 font-mono leading-none">${count} KPIs</div>
      </div>
    </td>`;
  };

  const rows = NAVS_ASPIRACIONALES.map(
    (nav) => {
      const navTotal = navCounts[nav.id] || 0;
      const rowOpacity = navTotal === 0 ? 'opacity-60' : '';
      return `
      <tr class="border-t border-slate-100 hover:bg-slate-50 ${rowOpacity}">
        <td class="px-4 py-3 align-top w-64">
          <div class="flex items-start gap-2">
            <span class="text-lg leading-none">${nav.icon}</span>
            <div>
              <div class="font-semibold text-slate-900 text-sm">${nav.id}</div>
              <div class="text-[11px] text-slate-500 leading-snug mt-0.5">${nav.desc}</div>
            </div>
          </div>
        </td>
        ${NIVELES.map((n) => cellRender(nav.id, n.n)).join('')}
      </tr>
    `;
    },
  ).join('');

  const totalsRow = `
    <tr class="border-t-2 border-slate-300 bg-slate-50 font-semibold">
      <td class="px-4 py-3 text-xs text-slate-700">Total Pulse</td>
      ${NIVELES.map((n) => {
        if (n.n === 'N0') {
          return `<td class="px-2 py-3 text-center border-l border-slate-200">
            <div class="text-xs font-bold text-slate-500">∞</div>
            <div class="text-[10px] text-slate-500 mt-0.5">sin doc.</div>
          </td>`;
        }
        const tot = n.n === 'N1' ? total : 0;
        const pct = n.n === 'N1' ? 100 : 0;
        return `<td class="px-2 py-3 text-center border-l border-slate-200">
          <div class="text-xs font-bold ${tot ? 'text-pulse-700' : 'text-slate-400'}">${pct}%</div>
          <div class="text-[10px] text-slate-500 font-mono mt-0.5">${tot}</div>
        </td>`;
      }).join('')}
    </tr>
  `;

  return `
    <section>
      <div class="flex items-baseline gap-3 mb-3">
        <span class="text-pulse-600 font-mono text-sm font-semibold">§ 1</span>
        <h2 class="text-2xl font-bold text-slate-900">Cómo imaginamos Pulse</h2>
      </div>
      <p class="text-slate-600 mb-6">
        La visión completa son <strong>6 navegaciones</strong> (cómo se accede a los datos) cruzadas con
        <strong>5 niveles de madurez del dato</strong> (qué tan refinado está cada indicador). El producto final llega cuando
        cada celda de esta matriz tiene contenido.
      </p>
      <div class="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table class="w-full text-xs">
          <thead class="bg-slate-50">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-slate-700">Navegación</th>
              ${headerNiveles}
            </tr>
          </thead>
          <tbody>
            ${rows}
            ${totalsRow}
          </tbody>
        </table>
        <div class="px-4 py-2 bg-slate-50 text-[11px] text-slate-500 border-t border-slate-200">
          <strong>6 × 5 = 30 celdas</strong> es el universo completo Pulse para cada módulo. N0 existe en abundancia (raw del transaccional) pero no está documentado; N1 es donde vivimos hoy con KPIs catalogados; N2→N4 está en construcción.
        </div>
      </div>

      <div class="mt-4 grid grid-cols-5 gap-3">
        ${NIVELES.map(
          (n) => `
          <div class="bg-white rounded-lg border border-slate-200 p-3">
            <div class="text-xs font-mono font-bold text-pulse-700">${n.n}</div>
            <div class="text-sm font-semibold text-slate-900 mt-0.5">${n.label}</div>
            <div class="text-[11px] text-slate-500 mt-1 leading-snug">${n.desc}</div>
          </div>
        `,
        ).join('')}
      </div>
    </section>
  `;
}

function renderSection2(navCounts: Record<string, number>, navProducts: Record<string, Set<string>>, total: number): string {
  const cards = NAVS_ASPIRACIONALES.map((nav) => {
    const count = navCounts[nav.id] || 0;
    const products = navProducts[nav.id] || new Set();
    const pct = total ? Math.round((count / total) * 100) : 0;
    const hasContent = count > 0;
    const cls = navColor(nav.id);
    return `
      <div class="rounded-xl border-2 ${cls} p-4 ${hasContent ? '' : 'opacity-60'}">
        <div class="flex items-start justify-between">
          <span class="text-2xl">${nav.icon}</span>
          ${hasContent
            ? `<span class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/80 border border-current">EN PRODUCCIÓN</span>`
            : `<span class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/80 border border-slate-300 text-slate-500">PENDIENTE</span>`}
        </div>
        <div class="font-bold text-base mt-2">${nav.id}</div>
        <div class="text-3xl font-bold mt-3">${count}</div>
        <div class="text-[11px] mt-0.5 opacity-80">KPIs catalogados · ${pct}% del total</div>
        ${products.size > 0
          ? `<div class="text-[10px] mt-2 opacity-70">${products.size} producto${products.size === 1 ? '' : 's'}: ${Array.from(products).slice(0, 3).join(', ')}${products.size > 3 ? '…' : ''}</div>`
          : `<div class="text-[10px] mt-2 opacity-70 italic">Aún sin instancias en Inventario</div>`}
      </div>
    `;
  }).join('');

  return `
    <section>
      <div class="flex items-baseline gap-3 mb-3">
        <span class="text-pulse-600 font-mono text-sm font-semibold">§ 2</span>
        <h2 class="text-2xl font-bold text-slate-900">Hoy estamos así — en navegaciones</h2>
      </div>
      <p class="text-slate-600 mb-6">
        De las 6 navegaciones imaginadas, hoy solo <strong>3 tienen contenido</strong>:
        Historical, Behavior y Explorer. Command Center, Compliance y Monitoring están
        <strong>pendientes en su totalidad</strong>.
      </p>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        ${cards}
      </div>
      <div class="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
        <strong>Lectura:</strong> el grueso del contenido vive en <strong>Behavior</strong> (lo que era Strategic).
        <strong>Command Center, Compliance y Monitoring siguen vacías</strong> — son los próximos focos del roadmap.
      </div>
    </section>
  `;
}

function renderSection3(total: number): string {
  type Estado = 'pre' | 'hoy' | 'proximo' | 'siguiente' | 'vision';
  const ESTADO_INFO: Record<Estado, { label: string; dotClass: string; ringClass: string; pillClass: string }> = {
    pre:       { label: 'Pre-historia',    dotClass: 'bg-slate-300 border-slate-400 border-dashed', ringClass: '',                           pillClass: 'bg-slate-100 text-slate-600' },
    hoy:       { label: 'Hoy',             dotClass: 'bg-pulse-500 border-pulse-600',               ringClass: 'ring-4 ring-pulse-200',      pillClass: 'bg-pulse-100 text-pulse-800' },
    proximo:   { label: 'En curso',        dotClass: 'bg-white border-pulse-400 border-2',          ringClass: '',                           pillClass: 'bg-amber-100 text-amber-800' },
    siguiente: { label: 'Siguiente',       dotClass: 'bg-white border-emerald-400 border-2',        ringClass: '',                           pillClass: 'bg-emerald-100 text-emerald-800' },
    vision:    { label: 'Visión',          dotClass: 'bg-white border-slate-300 border-2',          ringClass: '',                           pillClass: 'bg-slate-100 text-slate-500' },
  };
  const ESTACIONES: Array<{ n: typeof NIVELES[number]; estado: Estado; valor: string; sub: string }> = [
    { n: NIVELES[0], estado: 'pre',       valor: '∞',                sub: 'raw sin documentar' },
    { n: NIVELES[1], estado: 'hoy',       valor: `${total}`,         sub: 'KPIs catalogados' },
    { n: NIVELES[2], estado: 'proximo',   valor: '—',                sub: 'en perfilamiento' },
    { n: NIVELES[3], estado: 'siguiente', valor: '—',                sub: 'definiciones + justificación' },
    { n: NIVELES[4], estado: 'vision',    valor: '—',                sub: 'vinculado a estrategia' },
  ];

  const renderEstacion = (e: typeof ESTACIONES[number]) => {
    const info = ESTADO_INFO[e.estado];
    return `
      <div class="flex flex-col items-center flex-1 min-w-0 relative">
        <span class="text-[10px] font-semibold uppercase tracking-widest mb-3 px-2 py-0.5 rounded-full ${info.pillClass}">${info.label}</span>
        <div class="w-5 h-5 rounded-full border ${info.dotClass} ${info.ringClass} relative z-10"></div>
        <div class="mt-4 text-center">
          <div class="text-[10px] font-mono font-bold text-slate-500">${e.n.n}</div>
          <div class="text-sm font-bold text-slate-900 mt-0.5">${e.n.label}</div>
          <div class="text-2xl font-bold text-slate-900 mt-2 leading-none">${e.valor}</div>
          <div class="text-[10px] text-slate-500 mt-1 leading-tight">${e.sub}</div>
        </div>
      </div>
    `;
  };

  return `
    <section>
      <div class="flex items-baseline gap-3 mb-3">
        <span class="text-pulse-600 font-mono text-sm font-semibold">§ 3</span>
        <h2 class="text-2xl font-bold text-slate-900">Hoy estamos así — en niveles del dato</h2>
      </div>
      <p class="text-slate-600 mb-6">
        Tenemos <strong>mucho dato crudo (N0)</strong> en los transaccionales del cliente, pero no está documentado.
        Lo que sí tenemos catalogado hoy son <strong>${total} métricas en N1</strong> (cálculos identificados, contados, con descripción).
        Estamos en proceso de <strong>perfilar y llevar todo a N2</strong> (Monitoreo con contexto + visualización productiva),
        y después a <strong>N3 con definiciones formales y mucha justificación</strong>.
      </p>

      <div class="bg-white rounded-xl border border-slate-200 p-8">
        <div class="relative">
          <div class="absolute left-[10%] right-[10%] top-[42px] h-0.5 bg-gradient-to-r from-slate-200 via-pulse-300 to-slate-200" aria-hidden="true"></div>
          <div class="relative flex items-start justify-between gap-2">
            ${ESTACIONES.map(renderEstacion).join('')}
          </div>
        </div>
      </div>

      <div class="mt-6 relative">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-dashed border-slate-300"></div>
        </div>
        <div class="relative flex justify-center">
          <span class="bg-slate-50 px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Camino de madurez</span>
        </div>
      </div>

      <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div class="p-4 bg-slate-100 border border-slate-300 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <span class="badge badge-slate font-mono">N0 → N1</span>
            <span class="text-slate-500 text-[10px]">Hecho</span>
          </div>
          <div class="font-semibold text-slate-900 mb-1">Cataloga lo que existe</div>
          <p class="text-slate-700 leading-relaxed">
            Inventariar las métricas que ya se calculan sobre el dato raw del ERP/EHR.
            Darles nombre, descripción, módulo y producto. Es donde estamos hoy con ${total} entradas.
          </p>
        </div>

        <div class="p-4 bg-pulse-50 border border-pulse-200 rounded-lg ring-2 ring-pulse-300">
          <div class="flex items-center gap-2 mb-2">
            <span class="badge bg-pulse-200 text-pulse-900 font-mono">N1 → N2</span>
            <span class="badge badge-amber text-[10px]">En curso</span>
          </div>
          <div class="font-semibold text-pulse-900 mb-1">Perfilar para Monitoreo</div>
          <p class="text-pulse-800 leading-relaxed">
            Cada métrica recibe contexto (a qué proceso pertenece, qué pregunta responde),
            visualización productiva (gráfico apropiado en el dashboard) y se vuelve consumible diariamente.
          </p>
        </div>

        <div class="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <span class="badge badge-green font-mono">N2 → N3</span>
            <span class="text-emerald-600 text-[10px]">Siguiente</span>
          </div>
          <div class="font-semibold text-emerald-900 mb-1">Definir y justificar</div>
          <p class="text-emerald-800 leading-relaxed">
            Definiciones formales + justificación técnica/normativa + meta + umbrales + owner + notificación.
            A solicitud del cliente, por tenant. Es donde el indicador adquiere accountability.
          </p>
        </div>
      </div>
    </section>
  `;
}

function renderSection4(metrics: MetricRow[], data: AppData): string {
  const productGrouping = new Map<string, Map<string, { kpis: number; navs: Set<string> }>>();
  for (const m of metrics) {
    if (!m.Product || !m.Module) continue;
    if (!productGrouping.has(m.Product)) productGrouping.set(m.Product, new Map());
    const mods = productGrouping.get(m.Product)!;
    if (!mods.has(m.Module)) mods.set(m.Module, { kpis: 0, navs: new Set() });
    const cell = mods.get(m.Module)!;
    if (m.KPIDescription) cell.kpis++;
    if (m.DashboardType) cell.navs.add(m.DashboardType);
  }

  const skuProduct = new Map<string, { listoDev: boolean; prioridad: string }>();
  for (const s of data.skus) {
    if (s.Producto && !skuProduct.has(s.Producto)) {
      skuProduct.set(s.Producto, { listoDev: s.ListoDesarrollo, prioridad: s.Prioridad });
    }
  }

  const products = Array.from(productGrouping.keys()).sort((a, b) => {
    const aTotal = Array.from(productGrouping.get(a)!.values()).reduce((s, c) => s + c.kpis, 0);
    const bTotal = Array.from(productGrouping.get(b)!.values()).reduce((s, c) => s + c.kpis, 0);
    return bTotal - aTotal;
  });

  const cards = products
    .map((p) => {
      const mods = productGrouping.get(p)!;
      const totalKPIs = Array.from(mods.values()).reduce((s, c) => s + c.kpis, 0);
      const sku = skuProduct.get(p);
      const status = sku
        ? sku.listoDev
          ? `<span class="badge badge-green">Listo desarrollo</span>`
          : `<span class="badge badge-slate">En backlog</span>`
        : `<span class="badge badge-amber">Sin SKU</span>`;

      const modList = Array.from(mods.entries())
        .sort((a, b) => b[1].kpis - a[1].kpis)
        .map(
          ([modName, info]) => `
          <div class="flex items-center justify-between py-1.5 px-3 border-t border-slate-100 hover:bg-slate-50 text-xs">
            <span class="text-slate-700">${modName}</span>
            <span class="flex items-center gap-2 text-[11px]">
              <span class="text-slate-500">${info.kpis} KPI${info.kpis === 1 ? '' : 's'}</span>
              <span class="text-slate-400">·</span>
              <span class="text-slate-500">${info.navs.size}/6 nav</span>
            </span>
          </div>
        `,
        )
        .join('');

      return `
        <div class="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div>
              <div class="font-bold text-slate-900">${p}</div>
              <div class="text-[11px] text-slate-500 mt-0.5">${mods.size} módulo${mods.size === 1 ? '' : 's'} · ${totalKPIs} KPIs</div>
            </div>
            ${status}
          </div>
          <div>${modList}</div>
        </div>
      `;
    })
    .join('');

  return `
    <section>
      <div class="flex items-baseline gap-3 mb-3">
        <span class="text-pulse-600 font-mono text-sm font-semibold">§ 4</span>
        <h2 class="text-2xl font-bold text-slate-900">Alcance actual — módulos</h2>
      </div>
      <p class="text-slate-600 mb-6">
        Estos son los <strong>productos y módulos que ya tienen contenido catalogado</strong>.
        Cada módulo, en visión, debería tener sus 6 navegaciones y sus N0-N4. Aquí ves cuánto avance lleva cada uno.
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        ${cards}
      </div>
    </section>
  `;
}

function renderSection5(ejemploRows: MetricRow[]): string {
  const navsCubiertas = new Set(ejemploRows.map((m) => DASHTYPE_MAP_TO_NAV[m.DashboardType] || m.DashboardType).filter(Boolean));
  const owners = Array.from(new Set(ejemploRows.map((m) => m.OwnerCargo).filter(Boolean)));

  const navCards = NAVS_ASPIRACIONALES.map((nav) => {
    const has = navsCubiertas.has(nav.id);
    return `
      <div class="rounded-lg border-2 ${has ? navColor(nav.id) : 'bg-slate-50 border-dashed border-slate-300 text-slate-400'} p-3 text-center">
        <div class="text-xl ${has ? '' : 'grayscale opacity-50'}">${nav.icon}</div>
        <div class="text-xs font-semibold mt-1 ${has ? '' : 'line-through'}">${nav.id}</div>
        <div class="text-[10px] mt-0.5 ${has ? 'opacity-80' : 'opacity-60'}">${has ? 'En producción' : 'Pendiente'}</div>
      </div>
    `;
  }).join('');

  const kpisList = ejemploRows
    .slice(0, 12)
    .map(
      (m) => `
      <div class="flex items-start gap-3 py-2 px-4 border-t border-slate-100 hover:bg-slate-50">
        <span class="badge badge-blue mt-0.5 shrink-0">N1</span>
        <div class="flex-1 min-w-0">
          <div class="text-xs text-slate-800 leading-snug">${m.KPIDescription}</div>
          <div class="text-[10px] text-slate-500 mt-0.5">
            ${m.DashboardType ? `<span class="font-mono">${m.DashboardType}</span>` : ''}
            ${m.OwnerCargo ? ` · Owner: ${m.OwnerCargo}` : ''}
          </div>
        </div>
      </div>
    `,
    )
    .join('');

  const remaining = Math.max(0, ejemploRows.length - 12);

  return `
    <section>
      <div class="flex items-baseline gap-3 mb-3">
        <span class="text-pulse-600 font-mono text-sm font-semibold">§ 5</span>
        <h2 class="text-2xl font-bold text-slate-900">Ejemplo — Facturación / General</h2>
      </div>
      <p class="text-slate-600 mb-6">
        Caso concreto: el dashboard <strong>Facturación General</strong>, dentro del producto Facturación.
        Aquí ves cómo se ve hoy contra la visión completa.
      </p>

      <div class="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div class="px-6 py-4 bg-gradient-to-r from-pulse-50 to-white border-b border-slate-200">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-[10px] uppercase tracking-wider text-pulse-700 font-semibold">Producto · Dashboard</div>
              <div class="text-xl font-bold text-slate-900 mt-0.5">Facturación · Facturación General</div>
              <div class="text-xs text-slate-500 mt-1">
                ${ejemploRows.length} KPIs catalogados · ${navsCubiertas.size} de 6 navegaciones cubiertas
                ${owners.length ? ` · Owner: ${owners.join(', ')}` : ''}
              </div>
            </div>
            <span class="badge badge-amber">En desarrollo</span>
          </div>
        </div>

        <div class="p-6 border-b border-slate-200">
          <div class="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Navegaciones · Visión vs Hoy</div>
          <div class="grid grid-cols-3 md:grid-cols-6 gap-2">
            ${navCards}
          </div>
          <div class="mt-3 text-[11px] text-slate-500">
            ${navsCubiertas.size === 6
              ? 'Cobertura completa de navegaciones.'
              : `Faltan ${6 - navsCubiertas.size} navegaciones para la visión completa de este módulo.`}
          </div>
        </div>

        <div>
          <div class="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div class="text-xs font-semibold text-slate-700 uppercase tracking-wider">KPIs reales del dashboard · Todos N1</div>
            <div class="text-[11px] text-slate-500">Mostrando ${Math.min(12, ejemploRows.length)} de ${ejemploRows.length}</div>
          </div>
          ${kpisList || '<div class="p-6 text-sm text-slate-400 text-center">Sin KPIs catalogados para este módulo.</div>'}
          ${remaining > 0 ? `<div class="px-4 py-2 text-[11px] text-slate-500 text-center border-t border-slate-100">+ ${remaining} más</div>` : ''}
        </div>
      </div>

      <div class="mt-4 p-4 bg-pulse-50 border border-pulse-200 rounded-lg text-xs text-pulse-900">
        <strong>Lo que falta para llevar Facturación General a su visión completa:</strong>
        <ul class="mt-2 space-y-1 list-disc pl-5">
          <li>Cubrir las ${6 - navsCubiertas.size} navegaciones restantes (Command Center, Compliance, Monitoring son críticas para área financiera)</li>
          <li><strong>Perfilamiento N1 → N2:</strong> contextualizar cada métrica de facturación y darle visualización productiva para uso diario</li>
          <li><strong>Promoción N2 → N3:</strong> definiciones formales + meta + umbrales + notificación al Jefe de Facturación, con justificación técnica/normativa</li>
          <li>Vinculación a objetivos estratégicos (revenue, recaudo, glosas) para llegar a N4</li>
        </ul>
      </div>
    </section>
  `;
}

function renderFooter(): string {
  return `
    <div class="text-center text-xs text-slate-400 pt-8 border-t border-slate-200">
      Pulse Explorer · datos de <code>Inventario-Metricas-Pulse.csv</code> y <code>Portafolio-SKUs-Indigo.csv</code>
    </div>
  `;
}
